import { Card, CardContent, Typography } from '@mui/joy';
import { ChartOptions } from 'chart.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HorizontalBarChart, ITssDashboardFilters } from 'reports/components';
import { CHART_COLORS } from 'reports/constants';
import { useGetLinesByPart } from 'reports/hooks';
import { ILinesByPart } from 'reports/types';
import { Level, Variant } from 'theme/enums';

const DEFAULT_LIMIT = 10;

interface IProps {
  title: string;
  startDate: string;
  endDate: string;
  filters?: ITssDashboardFilters;
  height: number | string;
  limit?: number;
}

export const LinesByPartChart = ({
  title,
  startDate,
  endDate,
  filters,
  height,
  limit = DEFAULT_LIMIT,
}: IProps) => {
  const { t } = useTranslation();

  const requestPayload = useMemo(
    () => ({
      filters: {
        ...filters,
        jobCreatedAtSince: startDate,
        jobCreatedAtUntil: endDate,
      },
      limit,
    }),
    [startDate, endDate, filters, limit]
  );

  const { records, isLoading, isFetching } = useGetLinesByPart(requestPayload);

  const isDataLoading = isLoading || isFetching;

  const transformedData = useMemo(() => {
    if (!records.length) {
      return { datasets: [], labels: [] };
    }

    const labels = records.map((item: ILinesByPart) => item.partNumber);
    const data = records.map((item: ILinesByPart) => item.count);

    return {
      datasets: [
        {
          backgroundColor: CHART_COLORS.primary,
          data,
          label: t('labelLines'),
        },
      ],
      labels,
    };
  }, [records, t]);

  const chartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      plugins: {
        legend: {
          display: false,
        },
      },
    }),
    []
  );

  return (
    <Card variant={Variant.OUTLINED}>
      <CardContent>
        <Typography level={Level.TITLE_MD} sx={{ marginBottom: 2 }}>
          {title}
        </Typography>
        <HorizontalBarChart
          data={transformedData}
          isLoading={isDataLoading}
          height={height}
          options={chartOptions}
        />
      </CardContent>
    </Card>
  );
};
